import { TFunction } from "i18next";
import {
  getRoosterAutobookAvailabilityData,
  getRoosterAutobookAvailabilityMeta,
  getRoosterAutobookInterviewStatus,
  getRoosterLinkMeetingData,
  validateRoosterLinkService
} from "../../services/api/meeting.api";
import { Provider } from "../../services/api/types/roosterlinks.type";
import {
  HostType,
  MeetingLinkType,
  SchedulerStoreType,
  SessionDataType
} from "../../state/scheduler.store";
import { extractDateFromISODateTime } from "../../utils/dateTime";
import {
  isStaleRequestSessionAttempt,
  loadAvailabilityForAutobook
} from "../availability/availability";
import moment from "moment-timezone";
import { setUserGrantAsInvalidError } from "../../utils/errorManagement";

export const validateRoosterLinkHelper = async ({
  personIdentifier,
  profileIdentifier,
  userUid,
  link,
  meetingLink
}: {
  personIdentifier: string;
  profileIdentifier: string;
  userUid: string;
  link?: SchedulerStoreType["link"];
  error?: {
    code?: string;
    invitedBy?: string;
  };
  meetingLink?: MeetingLinkType;
}) => {
  const response = await validateRoosterLinkService({
    personIdentifier,
    profileIdentifier,
    userUid
  });

  const isValidatedViaApi = response?.code === "VALIDATED_SUCCESSFULLY";

  if (link?.scheduleId) {
    const roosterLinkMeetingData = await getRoosterLinkMeetingData({
      userId: personIdentifier,
      internalUserId: userUid,
      roosterLinkId: meetingLink?.scheduledData?.docDataRoosterLinks?.id ?? "",
      linkId: profileIdentifier,
      availabilityId: link.scheduleId ?? ""
    });

    const isCancelled = roosterLinkMeetingData?.docData?.cancelled ?? false;

    return {
      hasError: !isValidatedViaApi,
      error: {
        code: response?.code as string,
        invitedBy: response?.invitedBy as string
      },
      isCancelled,
      isValidatedViaApi
    };
  }

  return {
    hasError: !isValidatedViaApi,
    error: { code: response?.code, invitedBy: response?.invitedBy },
    isCancelled: false,
    isValidatedViaApi
  };
};

export const validateAutobookHelper = async ({
  clientId,
  host,
  sessionData,
  meetingLink,
  actions,
  link,
  providers,
  ui,
  t
}: {
  clientId: string;
  host: HostType;
  sessionData: SessionDataType;
  meetingLink: MeetingLinkType;
  link: SchedulerStoreType["link"];
  actions: SchedulerStoreType["actions"];
  providers: Provider[];
  ui: SchedulerStoreType["ui"];
  t: TFunction;
}) => {
  if (link?.scheduleId) {
    const autobookMeta = await getRoosterAutobookAvailabilityMeta({
      linkId: link?.scheduleId ?? ""
    });

    if (!autobookMeta?.isSenderGrantValid) {
      // Log invalid sender grant for troubleshooting
      console.warn(
        `[validateAutobookHelper] Invalid sender grant detected for scheduleId: ${link.scheduleId}`,
        { autobookMeta }
      );
      setUserGrantAsInvalidError(actions, t);
      return false;
    }

    // Validate reschedule parameters before proceeding with reschedule
    if (sessionData?.isRescheduleFlow) {
      const autobookAvailabilityData = await getRoosterAutobookAvailabilityData(
        {
          linkId: link?.scheduleId ?? ""
        }
      );

      // Check if reschedule is allowed
      if (
        autobookAvailabilityData &&
        "docData" in autobookAvailabilityData &&
        autobookAvailabilityData.docData?.reschedule === false
      ) {
        // Update the store with the latest reschedule status
        actions.setMeetingLink({
          ...meetingLink,
          canReschedule: false,
          rescheduleStatus:
            autobookAvailabilityData.docData?.rescheduleStatus ?? null
        });

        // Refresh the page to show updated state with disabled reschedule button
        window.location.reload();
        return false;
      }
    }
  }

  // Pass the session-start timestamp (not a fresh Date.now()), matching the
  // contract used by useAvailability. loadAvailabilityForAutobook's stale-
  // attempt guard compares mountTime against the store's
  // requestSessionStartedAt; a click-time Date.now() never matches, so the
  // guard would always treat this pre-booking validation as "stale" and
  // return null — stranding the Schedule button with no feedback.
  const mountTime = sessionData?.requestSessionStartedAt ?? Date.now();

  const result = await loadAvailabilityForAutobook({
    link,
    clientId,
    host,
    sessionData,
    meetingLink,
    actions,
    providers,
    skipSettingSessionData: true,
    ui,
    debug: { enabled: true, source: "pre_autobook_booking" },
    t,
    mountTime
  });

  const autobookAvailabilityData = await getRoosterAutobookAvailabilityData({
    linkId: link?.scheduleId ?? ""
  });

  if (
    autobookAvailabilityData &&
    "docData" in autobookAvailabilityData &&
    autobookAvailabilityData.docData?.cancelled
  ) {
    actions.setUiState({
      currentView: "month",
      currentStep: "initial",
      error: {
        code: "MEETING_CANCELLED",
        message: t("ROOSTER.PUBLIC-APP.INTERVIEW_CANCELLED", {
          defaultValue: "Interview Cancelled"
        }),
        description: t("ROOSTER.PUBLIC-APP.INTERVIEW_ALREADY_CANCELLED", {
          defaultValue: "The interview has already been cancelled."
        }),
        type: "persistent"
      }
    });
    return false;
  }

  if (result?.computedAvailability && sessionData?.selectedTimezone) {
    const currentSelectionISO = moment(sessionData?.selectedSlot?.date)
      .tz(sessionData?.selectedTimezone, true)
      .add(sessionData?.selectedSlot?.hour, "hours")
      .add(sessionData?.selectedSlot?.minute, "minutes")
      .format();

    const selectedSlot = sessionData.selectedSlot;
    const availability = result.computedAvailability.flat(1);

    let isSlotAvailable = false;

    if (
      ui?.currentView === "week" &&
      sessionData.selectedTimezone !== undefined
    ) {
      // Check if the current view is "week" view
      // Create an array of time ranges (start and end times) for each available slot
      const availabilityTimeRangesForWeekView = availability.flatMap((day) =>
        day.availability.map((slot) => {
          const timezone = sessionData.selectedTimezone ?? moment.tz.guess();
          const start = moment(day.date)
            .tz(timezone)
            .add(slot.hour, "hours")
            .add(slot.minute, "minutes");
          const end = moment(day.date)
            .tz(timezone)
            .add(slot.hour, "hours")
            .add(slot.minute, "minutes")
            .add(meetingLink?.availability?.duration ?? 30, "minutes");
          return {
            start: start.format(),
            end: end.format()
          };
        })
      );

      // Sort the ranges by start time
      const sortedRanges = [...availabilityTimeRangesForWeekView].sort((a, b) =>
        moment(a.start).diff(moment(b.start))
      );

      // Merge overlapping and adjacent ranges
      // Merge overlapping time ranges using reduce
      const mergedRanges = sortedRanges.reduce(
        (merged, current) => {
          // If merged array is empty, add the first range
          if (!merged.length) return [{ ...current }];

          // Get the last range from merged array
          const previous = merged[merged.length - 1];

          // If current range starts before or at the same time as previous range ends,
          // merge them by extending the end time of previous range
          if (
            moment(current.start)
              .clone()
              .isSameOrBefore(moment(previous.end).clone())
          ) {
            previous.end = moment
              .max(moment(previous.end).clone(), moment(current.end).clone())
              .tz(sessionData.selectedTimezone ?? moment.tz.guess())
              .format();
            return merged;
          }

          // If ranges don't overlap, add current range to merged array
          merged.push({ ...current });
          return merged;
        },
        [] as typeof availabilityTimeRangesForWeekView
      );

      // Calculate the end time of selected slot based on meeting duration
      const selectedSlotEnd = moment(currentSelectionISO)
        .add(meetingLink?.availability?.duration ?? 30, "minutes")
        .tz(sessionData.selectedTimezone ?? moment.tz.guess())
        .format();

      // Check if the selected time slot falls within any of the merged ranges
      const isSlotWithinRange = mergedRanges.some(
        (range) =>
          moment(currentSelectionISO).isSameOrAfter(range.start) &&
          moment(selectedSlotEnd).isSameOrBefore(range.end)
      );

      isSlotAvailable = isSlotWithinRange;
    } else {
      // For non-week views
      isSlotAvailable = availability.some((availableDate) => {
        const availableDateOnly = extractDateFromISODateTime(
          availableDate.date
        );
        const selectedDateOnly =
          extractDateFromISODateTime(currentSelectionISO);
        if (moment(availableDateOnly).isSame(moment(selectedDateOnly), "day")) {
          const slotTime = moment(availableDate.date)
            .hour(selectedSlot?.hour || 0)
            .minute(selectedSlot?.minute || 0);
          return availableDate.availability.some((slot) =>
            moment(availableDate.date)
              .hour(slot.hour)
              .minute(slot.minute)
              .isSame(slotTime)
          );
        }
        return false;
      });
    }

    const isSelectedSlotInFuture = moment(currentSelectionISO)
      .tz(sessionData?.selectedTimezone ?? moment.tz.guess())
      .isAfter(moment().tz(sessionData.selectedTimezone ?? moment.tz.guess()));

    if (!isSlotAvailable || !isSelectedSlotInFuture) {
      actions.setUiState({
        currentView: "month",
        currentStep: "initial",
        error: {
          code: "SLOT_NOT_AVAILABLE",
          message: t("ROOSTER.PUBLIC-APP.DATE_TIME_UNAVAILABLE", {
            defaultValue: "Date/Time No Longer Available"
          }),
          description: t("ROOSTER.PUBLIC-APP.SELECT_ANOTHER_DATE_TIME", {
            defaultValue:
              "This date/time is no longer available. Please select another date/time."
          }),
          type: "dismissible",
          primaryButtonText: t("ROOSTER.COMMON.OK", {
            defaultValue: "OK"
          })
        }
      });
      return false;
    }

    return true;
  }

  // If a newer scheduling attempt superseded this one, loadAvailabilityForAutobook
  // returns null via its stale guard — that newer attempt now owns the UI/state,
  // so bail silently and do NOT overwrite it with an error (mirrors the guard's
  // intent). Reuse the same mountTime + predicate so staleness stays consistent.
  if (isStaleRequestSessionAttempt(mountTime)) {
    return false;
  }

  // Availability could not be verified for a genuine reason:
  // loadAvailabilityForAutobook returned null (empty advanced-availability
  // response / unsupported availability type) or no timezone was selected.
  // Never fall through returning undefined — the caller treats a falsy result
  // as "invalid" and silently bails (`if (!isValid) return`), leaving the
  // Schedule button dead with no feedback. Surface a dismissible error and
  // return false instead.
  actions.setUiState({
    currentView: "month",
    currentStep: "initial",
    error: {
      code: "AVAILABILITY_UNVERIFIED",
      message: t("ROOSTER.PUBLIC-APP.UNABLE_TO_VERIFY_AVAILABILITY", {
        defaultValue: "Unable to verify availability"
      }),
      description: t("ROOSTER.PUBLIC-APP.UNABLE_TO_VERIFY_AVAILABILITY_DESC", {
        defaultValue:
          "We couldn't verify your selected time. Please try again or select another date/time."
      }),
      type: "dismissible",
      primaryButtonText: t("ROOSTER.COMMON.OK", {
        defaultValue: "OK"
      })
    }
  });
  return false;
};

export const validateAvailabilityLinkHelper = async ({
  link,
  actions,
  t
}: {
  link: SchedulerStoreType["link"];
  actions: SchedulerStoreType["actions"];
  t: TFunction;
}) => {
  const autobookAvailabilityData = await getRoosterAutobookAvailabilityData({
    linkId: link?.scheduleId ?? ""
  });

  if (
    autobookAvailabilityData &&
    "docData" in autobookAvailabilityData &&
    autobookAvailabilityData?.docData?.cancelled
  ) {
    actions.setUiState({
      currentView: "availability-slot",
      currentStep: "initial",
      error: {
        code: "MEETING_CANCELLED",
        message: t("ROOSTER.PUBLIC-APP.INTERVIEW_CANCELLED", {
          defaultValue: "Interview Cancelled"
        }),
        description: t("ROOSTER.PUBLIC-APP.INTERVIEW_ALREADY_CANCELLED", {
          defaultValue: "The interview has already been cancelled."
        }),
        type: "persistent"
      }
    });
    return false;
  }

  return true;
};

export const getAutobookInterviewStatusHelper = async ({
  link
}: {
  link: SchedulerStoreType["link"];
}) => {
  const status = await getRoosterAutobookInterviewStatus({
    linkId: link?.scheduleId ?? ""
  });
  return status;
};
